import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import {
    EditTaskResource,
    ResourceObj,
    TaskResource,
    TaskResourceNode,
    TrackerTaskParams,
} from '@app/interfaces/lm/kanban-board';
import { ResourceGroup } from '@app/interfaces/rm/resource-group';
import { ResourceType } from '@app/interfaces/rm/resources';
import { SelectOption } from '@app/interfaces/sys/dict';
import resources from '@app/modules/product/rm/resource/components/other-resources/resources';
import { KanbanBoardService } from '@services/lm/kanban-board.service';
import { ResourceGroupService } from '@services/rm/resource-group.service';
import { ResourcesService } from '@services/rm/resources.service';
import { ConfirmationService } from '@services/sys/confirmation.service';
import { GANTT_GLOBAL_CONFIG, GanttDate, GanttItem, GanttViewType, NgxGanttComponent } from '@worktile/gantt';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';

interface Task {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
}

interface Resource {
  id: string;
  name: string;
  type: string;
  children?: Task[];
  totalTaskCount: number;
  taskStartRow: number;
  taskEndRow: number;
  hasLoadMore: boolean;
  expanded?: boolean;
  expandable?: boolean;
}

type LoadedTasksMap = Map<string, Task[]>;
type LoadedResourceMap = Map<string, Resource[]>;

@Component({
    selector: 'app-task-resources',
    templateUrl: './task-resources.component.html',
    styleUrl: './task-resources.component.less',
    providers: [
        {
            provide: GANTT_GLOBAL_CONFIG,
            useValue: {
                locale: 'en',
                timeZone: 'America/New_York',
                dateFormat: {
                    day: 'MMM d',
                    hour: 'HH:mm',
                    month: 'MMM',
                    quarter: "'Q'q",
                    week: "'Week' w, d",
                    year: 'yyyy',
                    yearMonth: 'yyyy-MM',
                    yearQuarter: "yyyy-'Q'Q",
                },
            },
        },
    ],
})
export class TaskResourcesComponent implements OnInit, OnChanges {
    @ViewChild('gantt') ganttComponent!: NgxGanttComponent;
    @Input() value: TaskResource[] = [];
    @Input() options: string[] = [];
    @Input() className = '';
    @Input() fieldName = 'Resources';
    @Input() clickToEdit = true;
    @Input() isRequired = false;
    @Output() editSuccess = new EventEmitter();
    @Input() focusedField = '';
    @Input() isResource: { category: boolean } = { category: false };
    @Input() inactiveUsers: boolean = false;

    isEditing = false;
    saveLoading = false;
    currentValue: Array<EditTaskResource> = [];
    groupOptions: SelectOption[] = [];
    configResourceTypes: ResourceType[] = [];
    nodes: TaskResourceNode[] = [];
    configResourceTree: TaskResourceNode[] = [];
    configResourceTreeTemp: {
        [resourceType: string]: TaskResourceNode
    } = {};
    loading = false;
    treeResourceObj: ResourceObj = {};
    selectedGroups: Array<string | number> = [];
    groupList: ResourceGroup[] = [];
    initValue: EditTaskResource[] = [];
    groupListObj: {
        [groupId: number]: ResourceGroup
    } = {};
    allGroupSelectedInstanceIds: string[] = [];
    timer!: number | null;

    selectedResource: TaskResource[] = [];

    items: GanttItem[] = [];
    loadedTaskMap: LoadedTasksMap = new Map();
    loadedResourceMap: LoadedResourceMap = new Map();
    showGantt = false;
    //showLoadMore = false;
    dateDefault = dayjs().format('YYYY-MM-DD');
    earliestStart = new GanttDate(dayjs().startOf('day').toDate());
    latestEnd = new GanttDate(dayjs().endOf('day').toDate());
    viewOptions = {
        start: this.earliestStart,
        end: this.latestEnd,
        addAmount: 0
    }

    viewType: GanttViewType = GanttViewType.hour;

    constructor(private message: NzMessageService,
        private resourceGroupService: ResourceGroupService,
        private resourcesService: ResourcesService,
        private kanbanBoardService: KanbanBoardService,
        private confirmationService: ConfirmationService) {
    }

    async ngOnInit(): Promise<void> {
        try {
            this.loading = true;
            this.treeResourceObj = {};
            await Promise.all([this.getSettingResourceOptions(), this.getResourceGroupOptions(), this.getGroupList()]);
            if (this.value) {
                this.initData();
            }
        } finally {
            this.loading = false;
            this.setNoDataText();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.setNoDataText();
        const { value } = changes;
        if (value) {
            this.initData();
        }
        this.showGantt = this.focusedField === '' ? this.isResource.category && this.isEditing : this.focusedField === 'Resource';
        if (this.showGantt) {
          this.waitForDataLoad().then(() => {
            this.getGanttChart();
          })
        }
    }

    initData(): void {
        this.initValue = this.value && this.value.length > 0 ? this.setData() : [];
        this.currentValue = [...this.initValue];
        if (!this.clickToEdit && this.currentValue.length === 0) {
            this.addResource();
        }
    }

    /** 获取Group的所有数据 */
    async getGroupList(): Promise<void> {
        const { content } = await this.resourceGroupService.find({
            page: 0,
            size: 999999,
        });
        this.groupList = content || [];
        let obj = {};
        this.groupList.forEach(it => {
            obj = {
                ...obj,
                [it.id]: it,
            };
        });
        this.groupListObj = obj;
    }

    setNoDataText() {
        setTimeout(() => {
            const noDataEl = this.ganttComponent?.element.querySelector('.empty-text');
            if (noDataEl && noDataEl.textContent?.includes('没有数据')) {
                noDataEl.textContent = 'No Data';
            }
        });
    }

    /** 回显数据格式化 */
    setData(): Array<EditTaskResource> {
        const selectedGroups = [...new Set(this.value.map(it => it.resourceInstanceGroupId ? it.resourceInstanceGroupId : 'all'))];
        let obj: {
            [key: string]: TaskResource[]
        } = {};
        const keys: string[] = [];
        selectedGroups.forEach(groupType => {
            const isAll = groupType === 'all';
            const arr = this.value.filter(it => isAll ? (!it.resourceInstanceGroupId || it.resourceInstanceGroupId === 'all') : it.resourceInstanceGroupId === groupType);
            arr.forEach(it => {
                const { timeIntervals } = it;
                timeIntervals.forEach(timeItem => {
                    const timeStr = JSON.stringify(timeItem);
                    const key = `${groupType}_${timeStr}`;
                    if (keys.includes(key)) {
                        obj[key].push(it);
                    } else {
                        obj = {
                            ...obj,
                            [key]: [it]
                        };
                        keys.push(key);
                    }
                });
            });
        });
        // 根据时间段范围进行分组
        let timeGroup: {
            [resourcesStr: string]: any
        } = {};
        const groupResources: string[] = [];
        Object.keys(obj).forEach(key => {
            const arr = key.split('_');
            const resourceInstanceGroupId = arr[0];
            const timeInterval = JSON.parse(arr[1]);
            const isAll = resourceInstanceGroupId === 'all';
            const resources = obj[key];
            let options: any = [];
            let optionObj: any = {};
            if (isAll) {
                options = this.configResourceTree;
                optionObj = this.treeResourceObj;
            } else {
                const { resourceObj, resourceOptions } = this.getGroupResourceInstancesData(Number(resourceInstanceGroupId));
                options = resourceOptions;
                optionObj = resourceObj;
            }
            const resourcesStr = resources.map(it => Number(it.resourceInstanceId)).sort((a, b) => a - b).join(',');
            const groupId = isAll ? 'all' : Number(resourceInstanceGroupId);
            const groupResourceStr = `${groupId}_${resourcesStr}`;
            const info = {
                resourceInstanceGroupId: isAll ? 'all' : Number(resourceInstanceGroupId),
                resources: resources.map(xx => Number(xx.resourceInstanceId)),
                resourceNames: [...new Set(resources.map(xx => xx?.name))].join(','),
                resourceOptions: options,
                resourceObj: optionObj
            };
            if (!groupResources.includes(groupResourceStr)) {
                timeGroup = {
                    ...timeGroup,
                    [groupResourceStr]: {
                        info,
                        timeIntervals: [timeInterval]
                    }
                };
                groupResources.push(groupResourceStr);
            } else {
                timeGroup[groupResourceStr].timeIntervals.push(timeInterval);
            }
        });
        return Object.keys(timeGroup).map(key => {
            return {
                ...timeGroup[key].info,
                timeIntervals: timeGroup[key].timeIntervals
            };
        });
    }

    /** 获取Task Config配置Resource时选择的资源类型的所有资源 */
    async getSettingResourceOptions(): Promise<void> {
        const allResourceTypes = await this.resourcesService.findAllResourceType();
        this.configResourceTypes = allResourceTypes.filter((it: any) => this.options.includes(it?.resourceTypeKey || it?.key));
        this.configResourceTree = [];
        this.configResourceTreeTemp = {};
        const promises = this.configResourceTypes.map(it => this.fetchResourceList(it));
        await Promise.all(promises);
        const tempKeys = Object.keys(this.configResourceTreeTemp);
        this.configResourceTypes.forEach(it => {
            const resourceTypeKey = it?.resourceTypeKey || it?.key as string;
            if (!tempKeys.includes(resourceTypeKey)) {
                return;
            }
            const nodeData = this.configResourceTreeTemp[resourceTypeKey] || {} as TaskResourceNode;
            this.configResourceTree.push(nodeData);
        });
        this.nodes = this.configResourceTree || [];
        console.log('treeResourceObj', this.treeResourceObj);
    }

    async fetchResourceList(it: ResourceType): Promise<void> {
        const resourceKey = it?.key as string;
        const isCustomResource = resourceKey.indexOf('RESOURCE_INSTANCE') !== -1;
        const arr = await this.resourcesService.findResourceListByType(resourceKey, isCustomResource ? it.value : undefined);
        const resourceTypeKey = it?.resourceTypeKey || it?.key as string;
        this.configResourceTreeTemp = {
            ...this.configResourceTreeTemp,
            [resourceTypeKey]: {
                title: it?.label,
                value: resourceTypeKey,
                key: resourceTypeKey,
                disableCheckbox: true,
                children: arr.map(xx => {
                    const resourceInstance = xx?.resourceInstance;
                    const hardwareId = resourceInstance?.hardwareId || xx?.hardwareId;
                    const resourceInstanceId = resourceInstance?.id || xx?.id;
                    const resourceInfo = {
                        resourceInstanceId,
                        hardwareId,
                        hardwareType: resourceInstance?.hardwareType || xx?.resourceDefinitionTitle,
                        name: resourceInstance?.name || xx?.name
                    };
                    this.treeResourceObj = {
                        ...this.treeResourceObj,
                        [resourceInstanceId]: resourceInfo,
                    };
                    return {
                        title: xx.name,
                        value: resourceInstanceId,
                        key: resourceInstanceId,
                        isLeaf: true,
                        resourceInfo,
                    };
                }),
            } as TaskResourceNode
        };
    }

    /** 获取ResourceGroup的Options */
    async getResourceGroupOptions(): Promise<void> {
        const arr = await this.resourceGroupService.getOptions();
        this.groupOptions = [
            {
                label: 'All',
                value: 'all',
            },
            ...arr,
        ];
    }

    async startEdit(): Promise<void> {
        this.isEditing = true;
        if (!this.initValue || this.initValue.length === 0) {
            await this.waitForDataLoad();
        }
        if (this.currentValue.length === 0) {
            this.addResource();
        }
    }

    waitForDataLoad(): Promise<void> {
        return new Promise(resolve => {
            const checkInterval = setInterval(() => {
                if (!this.loading) {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 100); // 每 100ms 检查一次
        });
    }

    getGroupResourceInstancesData(groupId: number): {
    resourceOptions: SelectOption[],
    resourceObj: ResourceObj
} {
    const data = this.groupListObj?.[groupId];
    const resourceInstances = data ? data.resourceInstances || [] : [];
    let resourceObj = {};
    const resourceOptions = resourceInstances
        // 新增过滤逻辑：只保留配置允许的资源类型
        .filter(it => this.options.includes(it?.hardwareType !== 'RESOURCE_INSTANCE' ? it?.hardwareType : `${it?.hardwareType}_${it?.resourceDefinitionId}`))
        .map((it, index) => {
            const { name, id, hardwareType, resourceDefinitionTitle, hardwareId } = it;
            resourceObj = {
                ...resourceObj,
                [id]: {
                    resourceInstanceId: id,
                    hardwareId,
                    hardwareType: hardwareType !== 'RESOURCE_INSTANCE' ? hardwareType : resourceDefinitionTitle,
                    name,
                },
            };
            return {
                label: name,
                value: id,
            };
        });
    return {
        resourceOptions,
        resourceObj,
    };
}


    async addResource(): Promise<void> {
        if (!this.isEditing) {
            this.startEdit();
        }
        // const canSelectedGroups = this.groupOptions.filter(it => !this.selectedGroups.includes(it.value));
        // if (canSelectedGroups.length === 0) {
        //     this.message.warning('There are no optional groups available');
        //     return;
        // }
        // const resourceInstanceGroupId = canSelectedGroups[0].value;
        // let options = [];
        // let obj = {};
        // if (resourceInstanceGroupId === 'all') {
        //     options = this.configResourceTree;
        //     obj = this.treeResourceObj;
        // } else {
        //     const { resourceOptions, resourceObj } = this.getGroupResourceInstancesData(resourceInstanceGroupId as number);
        //     options = resourceOptions || [];
        //     obj = resourceObj || {};
        // }
        this.currentValue.push({
            resourceInstanceGroupId: '',
            resources: [],
            resourceOptions: [],
            resourceObj: {},
            timeIntervals: [
                {
                    startDate: null,
                    endDate: null,
                },
            ],
        });
        this.selectedGroups.push('');
    }

    addTime(testerIndex: number, timeIndex: number): void {
        const addedItem = {
            startDate: null,
            endDate: null,
        };
        this.currentValue[testerIndex].timeIntervals.splice(timeIndex + 1, 0, addedItem);
    }

    removeTime(testerIndex: number, timeIndex: number): void {
        this.currentValue[testerIndex].timeIntervals.splice(timeIndex, 1);
        this.getUpdatedDateRange();
    }

    removeRow(rowIndex: number): void {
        this.setNoDataText();
        this.currentValue.splice(rowIndex, 1);
        this.selectedGroups.splice(rowIndex, 1);
        this.updateResourceListGantt();
        this.getUpdatedDateRange();
    }

    /** 获取存在数据的行 */
    getDataRow(): EditTaskResource[] {
        return (this.currentValue).map((it, index) => {
            return {
                ...it,
                rowIndex: index + 1,
            };
        }).filter((it, index) => it.resourceInstanceGroupId || (it.resources || [])?.length > 0 || (it.timeIntervals || []).some(x => x.startDate || x.endDate));
    }

    /** 获取错误行的信息 */
    getValidRowMsg(rowIndex: number, type: string = 'resources'): string {
        const msg = `${this.fieldName}: The ${type} in row ${rowIndex} is empty.`;
        return msg;
    }

    /** 获取错误时间的信息 */
    getValidIntervalMsg(rowIndex: number, timeIndex: number, type: string = 'start date'): string {
        const msg = `${this.fieldName}: The ${type} in row ${rowIndex} is empty.`;
        return msg;
    }

    validForm(): boolean {
        const data = this.getDataRow();
        console.log('data', data);
        if (data.length === 0 && !this.isRequired) {
            return true;
        }
        if (data.length === 0 && this.isRequired) {
            this.message.warning('The Resource is required.');
            return false;
        }
        let flag = true;
        for (let i = 0; i < data.length; i++) {
            const { resourceInstanceGroupId, resources, timeIntervals } = data[i];
            if (!resourceInstanceGroupId || !resources || resources.length === 0) {
                flag = false;
                this.message.warning(this.getValidRowMsg(data[i]?.rowIndex as number, (!resourceInstanceGroupId ? 'group type' : 'resources')));
                break;
            }
            for (let j = 0; j < timeIntervals.length; j++) {
                const { startDate, endDate } = timeIntervals[j];
                const groupIndex = data[i]?.rowIndex as number;
                if (!startDate || !endDate) {
                    flag = false;
                    this.message.warning(this.getValidIntervalMsg(groupIndex, j + 1, !startDate ? 'start date' : 'end date'));
                    break;
                }
                if (dayjs(startDate).startOf('hour').valueOf() >= dayjs(endDate).startOf('hour').valueOf()) {
                    flag = false;
                    this.message.warning(`${this.fieldName}: The start date in row ${groupIndex} should not be later than the end date`);
                    break;
                }
            }
        }
        if (flag) {
            const validData = data.filter(item => item.resources && item.resources.length > 0) as {
                resources: number[];
                timeIntervals: { startDate: string; endDate: string }[];
            }[];

            if (this.hasResourceOverlap(validData)) {
                flag = false;
                this.message.warning('There are overlapping time intervals for the same resource.');
            }
        }
        return flag;
    }

    hasResourceOverlap(data: { resources: number[]; timeIntervals: { startDate: string; endDate: string }[] }[]): boolean {
        const resourceMap = new Map<number, { startDate: number; endDate: number }[]>();

        for (const row of data) {
            for (const resource of row.resources) {
                if (!resourceMap.has(resource)) {
                    resourceMap.set(resource, []);
                }
                const intervals = resourceMap.get(resource)!;
                for (const interval of row.timeIntervals) {
                    if (interval.startDate && interval.endDate) {
                        intervals.push({
                            startDate: new Date(interval.startDate).getTime(),
                            endDate: new Date(interval.endDate).getTime()
                        });
                    }
                }
            }
        }
        for (const intervals of resourceMap.values()) {
            intervals.sort((a, b) => a.startDate - b.startDate);
            for (let i = 0; i < intervals.length - 1; i++) {
                if (intervals[i].endDate > intervals[i + 1].startDate) {
                    return true;
                }
            }
        }

        return false;
    }

    handleSave(): void {
        const valid = this.validForm();
        if (!valid) {
            return;
        }
        this.confirmationService.confirmUpdateTask(this.inactiveUsers, () => {
                this.isEditing = false;
                if (this.initValue !== this.currentValue) {
                    const data = this.getParams();
                    this.editSuccess.emit(data);
                    data.forEach((param) => {
                        this.loadedTaskMap.delete(param.resourceInstanceId!.toString());
                    });
                }
                this.showGantt = false;
            }
        );
    }

    handleCancel(): void {
        this.isEditing = false;
        this.currentValue = this.initValue;
        this.showGantt = false;
    }

    validTime(testerIndex: number, timeIndex: number, changeType: string = 'start'): boolean {
        const timeSegment = this.currentValue[testerIndex].timeIntervals[timeIndex];
        const { startDate, endDate } = timeSegment;
        if (startDate && endDate && dayjs(startDate).startOf('hour').valueOf() >= dayjs(endDate).startOf('hour').valueOf()) {
            const msg = changeType === 'start' ? 'The start date should not be later than the end date' : 'The due date should not be earlier than start date';
            this.message.warning(msg);
            return false;
        }
        return true;
    }

    async changeGroup(row: EditTaskResource, index: number): Promise<void> {
        const { resourceInstanceGroupId } = row;
        row.resources = [];
        if (!resourceInstanceGroupId) {
            row.resourceOptions = [];
            row.resourceObj = {};
            this.selectedGroups.splice(index, 1, '');
        } else {
            const isAll = resourceInstanceGroupId === 'all';
            this.selectedGroups.splice(index, 1, isAll ? '' : resourceInstanceGroupId);
            if (isAll) {
                row.resourceOptions = this.configResourceTree;
            } else {
                const { resourceOptions, resourceObj } = this.getGroupResourceInstancesData(resourceInstanceGroupId as number);
                row.resourceOptions = resourceOptions;
                row.resourceObj = resourceObj;
            }
        }
    }

    convert(resourceTypes: string[], resourceIds: string[], taskStart: number, taskEnd: number, queryStartDate?: string, queryEndDate?: string): TrackerTaskParams {
        return {
            resourceTypeIn: resourceTypes,
            resourceInstanceIdIn: resourceIds,
            resourceEndRow: 50,
            resourceStartRow: 0,
            subtaskEndRow: taskEnd,
            subtaskStartRow: taskStart,
            startDate: queryStartDate || '',
            endDate: queryEndDate || '',
            taskEndRow: taskEnd,
            taskStartRow: taskStart,
            typeEndRow: 50,
            typeStartRow: 0,
        }
    }

    async getGanttChart() {
        this.setNoDataText();
        this.updateResourceListGantt();
        this.getUpdatedDateRange();

        this.selectedResource = this.getParams();

        // add currently selected resources to loadedResourceMap -- should also include resources with no tasks
        for (const {resourceInstanceId, name, hardwareType} of this.selectedResource) {
            if (!resourceInstanceId) {
                continue;
            }
            if (!this.loadedResourceMap.has(resourceInstanceId.toString())) {
                // add empty resource to loadedResourceMap
                const resource: Resource = {
                    id: resourceInstanceId.toString(),
                    name: name,
                    type: hardwareType,
                    totalTaskCount: 0,
                    children: [],
                    taskStartRow: 0,
                    taskEndRow: 50,
                    expanded: true,
                    expandable: true,
                    hasLoadMore: false
                }
                this.loadedResourceMap.set(resourceInstanceId.toString(), [resource]);
                this.convertResourceToGanttItem();

                // get children tasks and update resource map
                await this.getAllTaskData(0, 50);
                // after getting children tasks, add load more button if needed
                const resources = this.loadedResourceMap.get(resourceInstanceId.toString());
                if (!resources || resources.length === 0) {
                    console.error('Resource not found in map:', resourceInstanceId);
                    return;
                }
                const resourceInfo = resources[0];

                if (resourceInfo.taskEndRow < resourceInfo.totalTaskCount) {
                    const loadMore = {
                        id: `loadMore-${resourceInstanceId}`,
                        name: 'Load More',
                        startDate: new Date(0),
                        endDate: new Date(0),
                    };
                    this.loadedTaskMap.get(resourceInstanceId.toString())?.push(loadMore);
                    resource.hasLoadMore = true;
                }
                resource.children = this.loadedTaskMap.get(resourceInstanceId.toString());
                this.convertResourceToGanttItem();
            }
        }
    }

    async getAllTaskData(taskStart?: number, taskEnd?: number): Promise<void> {
        dayjs.extend(utc);
        const isoUtcStart = dayjs(this.earliestStart.getUnixTime() * 1000).utc().format();
        const isoUtcEnd = dayjs(this.latestEnd.getUnixTime() * 1000).utc().format();

        const resourceTypeList = new Set<string>();
        const resourceIdList = new Set<string>();

        this.selectedResource.forEach(({hardwareType, resourceInstanceId}) => {
            if (hardwareType) {
                resourceTypeList.add(hardwareType);
                resourceIdList.add(resourceInstanceId.toString());
            }
        })

        const response = await this.kanbanBoardService.loadAllTasksByResource(this.convert([...resourceTypeList], [...resourceIdList], taskStart || 0, taskEnd || 50, isoUtcStart, isoUtcEnd, ));

        response.flatMap(resource => resource.resources).forEach(resource => {
            const mappedTasks = resource.tasksArray?.map(task => ({
                id: task.taskId.toString(),
                title: task.taskName,
                timeIntervals: task.timeIntervals
            }));

            const mappedSubTasks = resource.subtasksArray?.map(subtask => ({
                id: subtask.subtaskId.toString(),
                title: subtask.subtaskName,
                timeIntervals: subtask.timeIntervals
            }));

            const allTasksArrayComb = [...(mappedTasks || []), ...(mappedSubTasks || [])];
            const resourceList = this.loadedResourceMap.get(resource.resourceId.toString());

            if (resourceList && resourceList[0]) {
                resourceList[0].totalTaskCount = resource.totalTasksCount + resource.totalSubtasksCount;
            }

            const existingTasks = this.loadedTaskMap.get(resource.resourceId.toString()) || [];
            const combinedTasks = [...existingTasks];

            allTasksArrayComb.forEach(task => {
                task.timeIntervals?.forEach((time, index) => {
                    const newTask = {
                        id: `${task.id}-${resource.resourceId}-${time.startDate}-${time.dueDate}`,
                        name: task.title,
                        startDate: new Date(time.startDate),
                        endDate: new Date(time.dueDate!)
                    };

                    const alreadyExists = combinedTasks.some(task => task.id === newTask.id);
                    if (!alreadyExists) {
                        combinedTasks.push(newTask);
                    }
                });
            });
            this.loadedTaskMap.set(resource.resourceId.toString(), combinedTasks);
        });
    }

    updateResourceListGantt() {
        const itemIds = this.items.map(item => { return item.id });
        const currentResources = this.getParams().map(value => { return value.resourceInstanceId?.toString() });

        // checks that gantt items are only showing what is in current values (selected users)
        itemIds.forEach((itemId) => {
            if (!currentResources.includes(itemId)) {
                const targetId = itemId;
                const indexInItems = this.items.findIndex(item => item.id === targetId);
                this.items.splice(indexInItems, 1);
                this.loadedResourceMap.delete(itemId);
                this.loadedTaskMap.delete(itemId);
            }
        })
        if (this.currentValue.length === 0) {
            this.showGantt = false;
        }
        this.convertResourceToGanttItem();
    }

    async updateTasksByDateRange() {
        for (const resourceItem of this.items) {
            const startQuery = this.loadedResourceMap.get(resourceItem.id)![0].taskStartRow || 0;
            const endQuery = this.loadedResourceMap.get(resourceItem.id)![0].taskEndRow || 50;

            await this.getAllTaskData(startQuery, endQuery);

            const filteredTasks = this.loadedTaskMap.get(resourceItem.id.toString())?.filter(task =>
                (task.endDate.getTime() >= this.earliestStart.getTime() && task.startDate.getTime() <= this.latestEnd.getTime())) || [];
            this.loadedResourceMap.get(resourceItem.id)?.forEach(resource => {
                resource.children = [...filteredTasks];
                if (!resource.hasLoadMore && resource.totalTaskCount > (resource.taskEndRow + 1)) {
                    resource.hasLoadMore = true;
                    const loadMore = {
                        id: `loadMore-${resourceItem.id}`,
                        name: 'Load More',
                        startDate: new Date(0),
                        endDate: new Date(0),
                    };
                    resource.children?.push(loadMore);
                } else {
                    resource.children = filteredTasks.filter(child => child.id !== `loadMore-${resource.id}`) ?? [];
                    resource.hasLoadMore = false;
                    if ((resource.taskEndRow + 1)  < resource.totalTaskCount) {
                        resource.hasLoadMore = true;
                        const loadMore = {
                            id: `loadMore-${resource.id}`,
                            name: 'Load More',
                            startDate: new Date(0),
                            endDate: new Date(0),
                        };
                        resource.children?.push(loadMore);
                    }
                }
                this.convertResourceToGanttItem();
            })
            this.convertResourceToGanttItem();
        }
    }

    getUpdatedDateRange() {
        // resets range to current day
        let earliest: GanttDate | null = null;
        let latest: GanttDate | null = null;
        this.viewType = GanttViewType.hour;

        this.currentValue.forEach((resource) => {
            resource.timeIntervals.forEach((timeInterval) => {
                if (timeInterval.startDate && timeInterval.endDate) {
                    let selectedStartDate = new GanttDate(timeInterval.startDate);
                    let selectedEndDate = new GanttDate(timeInterval.endDate);

                    if (!earliest || selectedStartDate.getUnixTime() < earliest.getUnixTime()) {
                        earliest = selectedStartDate;
                    }
                    if (!latest || selectedEndDate.getUnixTime() > latest.getUnixTime()) {
                        latest = selectedEndDate;
                    }
                }
            });
        });

        if (earliest && latest) {
            this.earliestStart = earliest;
            this.latestEnd = latest;
            setTimeout(() => {
              this.ganttComponent?.scrollToDate(this.earliestStart);
            }, 200);
        } else {
            this.earliestStart = new GanttDate(dayjs().startOf('day').toDate());
            this.latestEnd = new GanttDate(dayjs().endOf('day').toDate());
        }

        // determine view type based on date range
        const diff = this.latestEnd.getUnixTime() - this.earliestStart.getUnixTime();
        if (diff > 2629800 * 6) {
            this.viewType = GanttViewType.month;
        } else if (diff > 86400 * 4) {
            this.viewType = GanttViewType.day;
        } else {
            this.viewType = GanttViewType.hour;
        }
        this.viewOptions = {
            ...this.viewOptions,
            start: this.earliestStart,
            end: this.latestEnd,
            addAmount: 0
        };
        this.updateTasksByDateRange();
    }

    showLoadMore(resourceId: number): boolean {
        const resourceIdSplit = Number(resourceId.toString().split('-')[1]);
        const resourceMap = this.loadedResourceMap.get(resourceIdSplit.toString());
        return resourceMap?.some(resource => resource.hasLoadMore) || false;
    }

    loadMoreChild(resourceId: number) {
        const resourceIdSplit = Number(resourceId.toString().split('-')[1]);

        this.loadedResourceMap.get(resourceIdSplit.toString())?.forEach(resource => {
            resource.taskStartRow += 50;
            resource.taskEndRow += 50;
        })
        this.updateTasksByDateRange();
    }

    // convert tester map to ganttItem to show on gantt chart
    convertResourceToGanttItem(): void {
        const allResourceArray = [...this.loadedResourceMap.values()].flat();
        this.items = allResourceArray.map(resource => ({
            id: resource.id,
            title: resource.name,
            children: this.convertTaskToGanttItem(resource.children || []),
            expanded: resource.expanded,
            expandable: resource.expandable,
        }));
        this.items = [...this.items];
    }

    // convert task map to ganttItem to show on gantt chart
    convertTaskToGanttItem(task: Task[]): GanttItem[] {
        let children: GanttItem[] = [];
        task.forEach(taskData => {
            let croppedStart = 0;
            let croppedEnd = 0;
            if (taskData.name !== 'Load More') {
                croppedStart =  Math.max(new Date(taskData.startDate).getTime(), new Date(this.ganttComponent?.view.start.getUnixTime() * 1000).getTime());
                croppedEnd = Math.min(new Date(taskData.endDate).getTime(), new Date(this.ganttComponent?.view.end.getUnixTime() * 1000).getTime());
            }
            children.push({
                id: taskData.id,
                title: taskData.name,
                start: croppedStart,
                end: croppedEnd
            });
        });
        return children;
    }

    getParams(): TaskResource[] {
        const arr: TaskResource[] = [];
        const currentValue = this.currentValue || [] as any;
        const groupIds: string[] = [...new Set(currentValue.map(it => it.resourceInstanceGroupId as string))];

        groupIds.forEach(groupId => {
            const groupData = currentValue.filter(it => it.resourceInstanceGroupId === groupId);
            const resourceInstanceIds: number[] = [];
            const isGroupRow = groupId !== 'all';
            let groupObj: any = {};

            groupData.forEach(x => {
                const { timeIntervals, resourceObj } = x;
                const resources = x.resources || [];

                resources.forEach(resourceInstanceId => {
                    const resourceInfo = isGroupRow ? (resourceObj || {})[resourceInstanceId] : this.treeResourceObj[resourceInstanceId];

                    if (resourceInstanceIds.includes(resourceInstanceId)) {
                        groupObj[resourceInstanceId].timeIntervals = [
                            ...groupObj[resourceInstanceId].timeIntervals,
                            ...timeIntervals
                        ];
                    } else {
                        resourceInstanceIds.push(resourceInstanceId);
                        groupObj = {
                            ...groupObj,
                            [resourceInstanceId]: {
                                resourceInfo,
                                timeIntervals: [...timeIntervals]
                            }
                        };
                    }
                });
            });

            Object.keys(groupObj).forEach(resourceInstanceId => {
                const { resourceInfo, timeIntervals } = groupObj[resourceInstanceId];

                // Modify timeIntervals to set minutes and seconds to 00:00.000Z
                timeIntervals.forEach((interval: any) => {
                    if (interval.startDate) {
                        const startDate = new Date(interval.startDate);
                        startDate.setUTCMinutes(0, 0, 0);
                        interval.startDate = startDate.toISOString();
                    }
                    if (interval.endDate) {
                        const endDate = new Date(interval.endDate);
                        endDate.setUTCMinutes(0, 0, 0);
                        interval.endDate = endDate.toISOString();
                    }
                });

                arr.push({
                    resourceInstanceGroupId: isGroupRow ? groupId : null,
                    ...resourceInfo,
                    timeIntervals
                } as TaskResource);
            });
        });

        console.log('arr', arr);
        return arr;
    }



    // changeStartTime(testerIndex: number, timeIndex: number): void {
    //   const valid = this.validTime(testerIndex, timeIndex, 'start');
    //   if (!valid) {
    //     setTimeout(() => {
    //       this.currentValue[testerIndex].timeInterval[timeIndex].startTime = null;
    //     });
    //   }
    // }
    //
    //
    // changeDueTime(testerIndex: number, timeIndex: number): void {
    //   const valid = this.validTime(testerIndex, timeIndex, 'end');
    //   console.log('changeDueTime', testerIndex, timeIndex, valid);
    //   if (!valid) {
    //     setTimeout(() => {
    //       this.currentValue[testerIndex].timeInterval[timeIndex].dueTime = null;
    //     });
    //   }
    // }

    // showSelectedNode(node: NzTreeNode): string {
    //     const str = node.title || '';
    //     return str.length > 20 ? str.substr(0, 20) + '...' : str;
    // }
}
